import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Search, Edit, Trash2, MapPin } from 'lucide-react';
import { Airport, CreateAirportRequest } from '../interfaces/Airport';
import { airportService } from '../services/airportService';
import { DefaultLoader } from '../components';

const Airports: React.FC = () => {
  const [airports, setAirports] = useState<Airport[]>([]);
  const [filteredAirports, setFilteredAirports] = useState<Airport[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAirport, setEditingAirport] = useState<Airport | null>(null);
  const [formData, setFormData] = useState<CreateAirportRequest>({
    icao: '',
    iata: '',
    name: '',
    city: '',
    state: '',
    country: '',
    tz: ''
  });

  useEffect(() => {
    loadAirports();
  }, []);

  useEffect(() => {
    const filtered = airports.filter(airport =>
      airport.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      airport.iata.toLowerCase().includes(searchTerm.toLowerCase()) ||
      airport.icao.toLowerCase().includes(searchTerm.toLowerCase()) ||
      airport.city.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredAirports(filtered);
  }, [airports, searchTerm]);

  const loadAirports = async () => {
    try {
      setLoading(true);
      const data = await airportService.findAll();
      setAirports(data);
      setFilteredAirports(data);
    } catch (error) {
      console.error('Error loading airports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingAirport) {
        await airportService.update(editingAirport.id, formData);
      } else {
        await airportService.create(formData);
      }
      
      resetForm();
      loadAirports();
    } catch (error) {
      console.error('Error saving airport:', error);
    }
  };

  const handleEdit = (airport: Airport) => {
    setEditingAirport(airport);
    setFormData({
      icao: airport.icao,
      iata: airport.iata,
      name: airport.name,
      city: airport.city,
      state: airport.state,
      country: airport.country,
      tz: airport.tz
    });
    setShowAddForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este aeroporto?')) {
      try {
        await airportService.delete(id);
        loadAirports();
      } catch (error) {
        console.error('Error deleting airport:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      icao: '',
      iata: '',
      name: '',
      city: '',
      state: '',
      country: '',
      tz: ''
    });
    setEditingAirport(null);
    setShowAddForm(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div style={{ padding: '24px' }}>
      <div className="d-flex justify-between align-center mb-4">
        <div>
          <h1 style={{ 
            fontSize: 'var(--font-size-24)', 
            fontWeight: '600', 
            color: 'var(--color-dimgray)', 
            marginBottom: '8px',
            fontFamily: 'var(--font-inter)' 
          }}>
            Gerenciamento de Aeroportos
          </h1>
          <p style={{ 
            fontSize: 'var(--font-size-14)', 
            color: 'var(--color-darkgray)', 
            margin: '0',
            fontFamily: 'var(--font-inter)' 
          }}>
            Gerencie os aeroportos do sistema
          </p>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setShowAddForm(true)}
          className="btn btn-primary"
          style={{ fontFamily: 'var(--font-inter)' }}
        >
          <Plus size={18} />
          Adicionar Aeroporto
        </motion.button>
      </div>

      <div className="card">
        <div className="d-flex gap-2 mb-3">
          <div className="w-full" style={{ position: 'relative' }}>
            <Search 
              style={{ 
                position: 'absolute', 
                left: '12px', 
                top: '50%', 
                transform: 'translateY(-50%)', 
                color: 'var(--color-darkgray)' 
              }} 
              size={16} 
            />
            <input
              type="text"
              placeholder="Buscar por nome, código IATA, ICAO ou cidade..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input"
              style={{ paddingLeft: '40px', fontFamily: 'var(--font-inter)' }}
            />
          </div>
        </div>

        {loading ? (
          <DefaultLoader title="Aeroportos" messages={['Carregando aeroportos...', 'Processando dados...', 'Organizando informações...']} />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th style={{ fontFamily: 'var(--font-inter)', fontSize: 'var(--font-size-14)', fontWeight: '600' }}>ICAO</th>
                  <th style={{ fontFamily: 'var(--font-inter)', fontSize: 'var(--font-size-14)', fontWeight: '600' }}>IATA</th>
                  <th style={{ fontFamily: 'var(--font-inter)', fontSize: 'var(--font-size-14)', fontWeight: '600' }}>Nome</th>
                  <th style={{ fontFamily: 'var(--font-inter)', fontSize: 'var(--font-size-14)', fontWeight: '600' }}>Cidade</th>
                  <th style={{ fontFamily: 'var(--font-inter)', fontSize: 'var(--font-size-14)', fontWeight: '600' }}>Estado</th>
                  <th style={{ fontFamily: 'var(--font-inter)', fontSize: 'var(--font-size-14)', fontWeight: '600' }}>País</th>
                  <th style={{ fontFamily: 'var(--font-inter)', fontSize: 'var(--font-size-14)', fontWeight: '600' }}>Fuso</th>
                  <th style={{ fontFamily: 'var(--font-inter)', fontSize: 'var(--font-size-14)', fontWeight: '600' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredAirports.map((airport) => (
                  <motion.tr
                    key={airport.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    style={{ fontFamily: 'var(--font-inter)' }}
                  >
                    <td style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-12)', fontWeight: '600', color: 'var(--color-dimgray)' }}>
                      {airport.icao}
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-14)', fontWeight: '700', color: 'var(--color-dodgerblue)' }}>
                      {airport.iata}
                    </td>
                    <td style={{ fontSize: 'var(--font-size-14)', fontWeight: '500', color: 'var(--color-dimgray)' }}>
                      {airport.name}
                    </td>
                    <td style={{ fontSize: 'var(--font-size-14)' }}>
                      <div className="d-flex align-center gap-1">
                        <MapPin size={14} style={{ color: 'var(--color-darkgray)' }} />
                        <span style={{ color: 'var(--color-dimgray)' }}>{airport.city}</span>
                      </div>
                    </td>
                    <td style={{ fontSize: 'var(--font-size-14)', color: 'var(--color-dimgray)' }}>{airport.state}</td>
                    <td style={{ fontSize: 'var(--font-size-14)', color: 'var(--color-dimgray)' }}>{airport.country}</td>
                    <td style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', fontFamily: 'monospace' }}>
                      {airport.tz}
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <button
                          onClick={() => handleEdit(airport)}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: '6px',
                            cursor: 'pointer',
                            borderRadius: 'var(--br-4)',
                            color: 'var(--color-darkgray)',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f0f9ff';
                            e.currentTarget.style.color = 'var(--color-dodgerblue)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = 'var(--color-darkgray)';
                          }}
                          title="Editar"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(airport.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: '6px',
                            cursor: 'pointer',
                            borderRadius: 'var(--br-4)',
                            color: 'var(--color-darkgray)',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#fef2f2';
                            e.currentTarget.style.color = 'var(--color-red)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = 'var(--color-darkgray)';
                          }}
                          title="Excluir"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
            {filteredAirports.length === 0 && !loading && (
              <div className="text-center" style={{ padding: '48px 0' }}>
                <p style={{ 
                  color: 'var(--color-darkgray)', 
                  fontSize: 'var(--font-size-14)',
                  fontFamily: 'var(--font-inter)',
                  margin: '0' 
                }}>
                  Nenhum aeroporto encontrado
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de Adicionar/Editar Aeroporto */}
      {showAddForm && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: '50',
          padding: '16px'
        }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{
              backgroundColor: 'var(--color-white)',
              borderRadius: 'var(--br-8)',
              padding: '24px',
              width: '100%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflowY: 'auto',
              fontFamily: 'var(--font-inter)',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            <h2 style={{
              fontSize: 'var(--font-size-20)',
              fontWeight: '600',
              color: 'var(--color-dimgray)',
              marginBottom: '24px',
              fontFamily: 'var(--font-inter)'
            }}>
              {editingAirport ? 'Editar Aeroporto' : 'Adicionar Novo Aeroporto'}
            </h2>
            
            <form onSubmit={handleSubmit}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                gap: '16px', 
                marginBottom: '24px' 
              }}>
                <div className="form-group">
                  <label className="form-label">
                    Código ICAO *
                  </label>
                  <input
                    type="text"
                    name="icao"
                    value={formData.icao}
                    onChange={handleInputChange}
                    required
                    maxLength={4}
                    className="form-input"
                    placeholder="SBGR"
                    style={{ textTransform: 'uppercase', fontFamily: 'monospace' }}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">
                    Código IATA *
                  </label>
                  <input
                    type="text"
                    name="iata"
                    value={formData.iata}
                    onChange={handleInputChange}
                    required
                    maxLength={3}
                    className="form-input"
                    placeholder="GRU"
                    style={{ textTransform: 'uppercase', fontFamily: 'monospace' }}
                  />
                </div>
                
                <div style={{ gridColumn: '1 / -1' }} className="form-group">
                  <label className="form-label">
                    Nome do Aeroporto *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="Aeroporto Internacional de São Paulo/Guarulhos"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">
                    Cidade *
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="Guarulhos"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">
                    Estado *
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    required
                    maxLength={2}
                    className="form-input"
                    placeholder="SP"
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">
                    País *
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    required
                    maxLength={2}
                    className="form-input"
                    placeholder="BR"
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">
                    Fuso Horário *
                  </label>
                  <input
                    type="text"
                    name="tz"
                    value={formData.tz}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="America/Sao_Paulo"
                    style={{ fontFamily: 'monospace' }}
                  />
                </div>
              </div>
              
              <div className="d-flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={resetForm}
                  className="btn btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  {editingAirport ? 'Atualizar' : 'Criar'} Aeroporto
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Airports;